class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.notification.addEventListener('click', (evt) => {
      const qtyBtn = evt.target.closest('.cart-notification-qty-btn, .cart-notification-remove-btn');
      if (qtyBtn) {
        this.handleQtyButtonClick(qtyBtn);
      }
    });
  }

  async handleQtyButtonClick(btn) {
    if (btn.disabled) return;

    const action = btn.dataset.action;
    const itemKey = btn.dataset.itemKey;
    const currentQty = parseInt(btn.dataset.qty || 1, 10);
    const maxQty = parseInt(btn.dataset.max, 10) || 9999;

    let newQty = currentQty;
    if (action === 'remove') {
      newQty = 0;
    } else if (action === 'plus') {
      if (currentQty >= maxQty) {
        btn.disabled = true;
        return;
      }
      newQty = currentQty + 1;
    } else if (action === 'minus') {
      newQty = Math.max(0, currentQty - 1);
    }

    if (newQty === currentQty && action !== 'remove') return;

    btn.disabled = true;

    const cartItem = btn.closest('.cart-item');
    if ((action === 'remove' || newQty === 0) && cartItem) {
      cartItem.classList.add('is-removing');
    }

    try {
      const response = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: newQty,
          sections: this.getSectionsToRender().map((section) => section.id)
        })
      });

      const parsedState = await response.json();
      if (parsedState.errors) {
        if (cartItem) cartItem.classList.remove('is-removing');
        alert(parsedState.errors);
        return;
      }

      this.renderContents(parsedState);
    } catch (error) {
      if (cartItem) cartItem.classList.remove('is-removing');
      console.error('Error updating quantity:', error);
    }
  }

  open() {
    this.style.display = 'block';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('active');
        document.body.classList.add('cart-notification-open');
        document.documentElement.classList.add('cart-notification-open');

        if (this.notification) this.notification.classList.add('animate', 'active');
        const wrapper = this.querySelector('.cart-notification-wrapper');
        if (wrapper) wrapper.classList.add('active');
      });
    });

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus({ preventScroll: true });
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    this.dispatchCartViewEvent();
  }

  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('cart-notification-open');
    document.documentElement.classList.remove('cart-notification-open');

    if (this.notification) this.notification.classList.remove('active');
    const wrapper = this.querySelector('.cart-notification-wrapper');
    if (wrapper) wrapper.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);

    setTimeout(() => {
      if (!this.notification.classList.contains('active') && !this.classList.contains('active')) {
        this.style.display = 'none';
      }
    }, 400);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    const isCartEmpty = parsedState.item_count === 0 || (parsedState.items && parsedState.items.length === 0);

    this.getSectionsToRender().forEach((section) => {
      const targetElem = document.getElementById(section.id);
      if (targetElem && parsedState.sections && parsedState.sections[section.id]) {
        targetElem.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    const subtotalTemplate = document.getElementById('cart-subtotal-template');
    const subtotalTarget = document.getElementById('cart-notification-subtotal');
    if (subtotalTemplate && subtotalTarget) {
      subtotalTarget.innerHTML = subtotalTemplate.innerHTML;
    }

    const checkoutBtn = this.querySelector('button[name="checkout"]');
    if (checkoutBtn && parsedState.item_count !== undefined) {
      checkoutBtn.disabled = isCartEmpty;
    }

    if (isCartEmpty) {
      setTimeout(() => {
        this.close();
      }, 300);
      return;
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product' },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const elem = doc.querySelector(selector);
    return elem ? elem.innerHTML : html;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
